import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { getGridDateOperators, GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { IcDotSpread } from '@/assets/images';
import { DATE_FORMAT } from '@/config';
import {
  Disposition,
  DispositionFriendlyNames,
  IMasterNamePerson,
  IWantedPerson,
  IWantedPersonInvolvement,
  ProcessType,
  ProcessTypeFriendlyNames,
} from '@/models';
import { formatPersonName } from '@/utils';
import { formatDateOnly } from '@/utils/datetime';

const recordNumber = (): Partial<GridColDef> => ({
  field: 'recordNumber',
  renderCell: (params) => params.row.recordNumber || '-',
  sortable: true,
});

const getPersonName = (person: IWantedPersonInvolvement): string => {
  const mp = person.masterPerson;
  if (!mp || typeof mp === 'string') return '';
  const typed = mp as IMasterNamePerson;
  return formatPersonName(typed);
};

const names = (): Partial<GridColDef> => ({
  field: 'names',
  sortable: false,
  renderCell: (params: { row: IWantedPerson }) => {
    const persons = params.row.persons;
    if (!persons || !Array.isArray(persons) || persons.length === 0) return '-';

    const allNames = persons.map(getPersonName).filter(Boolean);

    if (allNames.length === 0) return '-';

    const visibleNames = allNames.slice(0, 2);
    const remaining = allNames.length - 2;

    const tooltipContent = allNames.join('\n');

    return (
      <Tooltip
        title={<Box sx={{ whiteSpace: 'pre-line' }}>{tooltipContent}</Box>}
        arrow
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {visibleNames.join(', ')}
          </Box>
          {remaining > 0 && (
            <Chip
              label={`+${remaining}`}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.75rem',
                flexShrink: 0,
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  },
});

const processType = (): Partial<GridColDef> => ({
  field: 'processType',
  renderCell: (params) =>
    ProcessTypeFriendlyNames[params.row.processType as ProcessType] || '-',
});
const disposition = (): Partial<GridColDef> => ({
  field: 'disposition',
  renderCell: (params) =>
    DispositionFriendlyNames[params.row.disposition as Disposition] || '-',
});
const dateIssued = (): Partial<GridColDef> => ({
  field: 'dateIssued',
  renderCell: (params) => {
    return params.row.dateIssued ? formatDateOnly(params.row.dateIssued) : '-';
  },
  filterOperators: getGridDateOperators(true),
});
const issuingCourt = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.issuingCourt?.name || '-',
});
const assignedOfficer = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.assignedOfficer?.fullName || '-',
});
const lastAttempt = (): Partial<GridColDef> => ({
  renderCell: (params: { row: IWantedPerson }) => {
    const attempts = [...(params.row.attempts || [])];

    // sort attempts by date in descending order
    attempts.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA; // descending order
    });

    return params.row.attempts?.[0]?.date
      ? `${moment(attempts?.[0]?.date).format(DATE_FORMAT)}`
      : '-';
  },
});

const status = (): Partial<GridColDef> => ({
  field: 'status',
  renderCell: (params) => {
    if (params.row.status === 'ACTIVE') return 'Active';
    if (params.row.status === 'INACTIVE') return 'Inactive';
    return '-';
  },
  sortable: true,
});

const actions = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void,
): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        openMenu?.(e, params.row);
      }}
    >
      <IcDotSpread />
    </IconButton>
  ),
});
export const wantedpersonCols = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void,
) => {
  return {
    recordNumber,
    names,
    processType,
    disposition,
    issuingCourt,
    assignedOfficer,
    lastAttempt,
    dateIssued,
    status,
    actions: () => actions(openMenu),
  };
};
